import axios from 'axios';
import config from '../config';

const API_URL = `${config.API_BASE_URL}`;
const getHeaders = () => {
  const headers = {};
  const m = window.location.pathname.match(/\/t\/([^/]+)/);
  if (m) headers['X-Target-Tenant-ID'] = m[1];
  return headers;
};
export const auditService = {
    // --- EVIDENCE API ---
    getEvidence: async () => {
        const res = await axios.get(`${API_URL}/evidence`, { headers: getHeaders() });
        return res.data;
    },

    reviewEvidence: async (evidenceId, reviewData) => {
        const res = await axios.put(`${API_URL}/evidence/${evidenceId}/review`, reviewData, {
            headers: getHeaders()
        });
        return res.data;
    },

    initiateAudit: async (auditData) => {
        const res = await axios.post(`${API_URL}/audits/initiate`, auditData, {
            headers: getHeaders()
        });
        return res.data;
    },

    // --- SCOPE JUSTIFICATION API (Strict Isolation) ---
    getScopeJustifications: async (standard) => {
        try {
            const res = await axios.get(`${API_URL}/scope/justifications`, {
                headers: getHeaders(),
                params: { standard_type: standard } // Strict Server-Side Filter
            });
            return res.data;
        } catch (error) {
            console.error("Failed to fetch scope justifications:", error);
            // Fallback to empty list to prevent crash
            return [];
        }
    },

    saveScopeJustification: async (data) => {
        // data = { standard_type, criteria_id, reason_code, justification_text }
        const res = await axios.post(`${API_URL}/scope/justifications`, data, {
            headers: getHeaders()
        });
        return res.data;
    },

    // --- AUDITOR PORTAL API ---
    getSoaSnapshot: async (framework = 'ISO27001', sessionId = null) => {
        const res = await axios.get(`${API_URL}/auditor/soa-snapshot`, {
            headers: getHeaders(),
            params: { framework, ...(sessionId && { session_id: sessionId }) }
        });
        return res.data;
    },

    // --- SOVEREIGN EVIDENCE SAMPLING (BYOS + dual-key) ---
    // Mint a short-lived presigned URL for the latest sampled evidence
    // checkpoint of a control inside an audit session. The auditor is gated
    // behind is_client_released (403 "Access Pending…") until the client
    // admin releases it. Errors propagate so the caller can branch on
    // err.response.status (403 locked, 404 no sample, 502 BYOS pending).
    downloadControlSample: async (sessionId, controlId) => {
        const res = await axios.get(
            `${API_URL}/auditor-portal/sessions/${encodeURIComponent(sessionId)}/controls/${encodeURIComponent(controlId)}/download-sample`,
            { headers: getHeaders() }
        );
        return res.data;
    },

    // Client Administrator releases the latest sample to the auditor
    // (flips is_client_released = TRUE). Auditors are forbidden (403).
    releaseControlSample: async (sessionId, controlId) => {
        const res = await axios.patch(
            `${API_URL}/auditor-portal/sessions/${encodeURIComponent(sessionId)}/controls/${encodeURIComponent(controlId)}/release-sample`,
            {},
            { headers: getHeaders() }
        );
        return res.data;
    },

    // Raw point-in-time configuration snapshot for a control. Returns a
    // 15-min presigned URL (S3/BYOS) + the raw JSON inline for native
    // rendering. Auditor gets 403 until the client admin releases.
    getControlRawSnapshot: async (sessionId, controlId) => {
        const res = await axios.get(
            `${API_URL}/auditor-portal/sessions/${encodeURIComponent(sessionId)}/controls/${encodeURIComponent(controlId)}/raw-snapshot`,
            { headers: getHeaders() }
        );
        return res.data;
    },

    // Management list of every sampled control in a session + the master
    // switch state. Drives the Evidence Release panel.
    getSessionSamples: async (sessionId) => {
        const res = await axios.get(
            `${API_URL}/auditor-portal/sessions/${encodeURIComponent(sessionId)}/samples`,
            { headers: getHeaders() }
        );
        return res.data;
    },

    // Master switch — release ALL sampled evidence in the session (current +
    // future) to the assigned auditor in one toggle.
    setSessionAutoRelease: async (sessionId, enabled) => {
        const res = await axios.patch(
            `${API_URL}/auditor-portal/sessions/${encodeURIComponent(sessionId)}/evidence-auto-release`,
            { enabled },
            { headers: getHeaders() }
        );
        return res.data;
    },

    // Bulk release — scope 'all' | 'pending' | 'selected' (+ control_ids).
    releaseSamplesBulk: async (sessionId, scope = 'pending', controlIds = null) => {
        const res = await axios.patch(
            `${API_URL}/auditor-portal/sessions/${encodeURIComponent(sessionId)}/release-samples`,
            { scope, ...(controlIds ? { control_ids: controlIds } : {}) },
            { headers: getHeaders() }
        );
        return res.data;
    },

    // Per-control override lock — withhold (or un-withhold) one control's
    // evidence even when the master switch is ON.
    withholdControlSample: async (sessionId, controlId, withheld) => {
        const res = await axios.patch(
            `${API_URL}/auditor-portal/sessions/${encodeURIComponent(sessionId)}/controls/${encodeURIComponent(controlId)}/withhold`,
            { withheld },
            { headers: getHeaders() }
        );
        return res.data;
    },

    // 2026-05-04 — Integrated Auditor Portal (Phase 0/1).
    // Returns one consolidated payload: session, frameworks, controls,
    // intent crosswalk, evidence_map, reviews. Single source of truth
    // for the rebuilt portal — replaces the separate getSoaSnapshot +
    // getDashboardStats + getReviews fan-out for new sessions.
    getAuditData: async (sessionId) => {
        const res = await axios.get(
            `${API_URL}/auditor/sessions/${sessionId}/audit-data`,
            { headers: getHeaders() }
        );
        return res.data;
    },

    // 2026-05-05 Phase 2c — Atomic unified-requirement verdict save.
    // Writes audit_reviews rows for EVERY mapped control_id of a unified
    // requirement in a single DB transaction. Source control receives
    // the direct verdict; other mapped controls receive the same
    // verdict tagged inherited_via='intent_crosswalk' (auditor must
    // accept) — except for MAJOR_NC / MINOR_NC where failures cascade
    // hard with no provisional softening.
    saveUnifiedReview: async (payload) => {
        const res = await axios.post(
            `${API_URL}/auditor/unified-review`,
            payload,
            { headers: getHeaders() }
        );
        return res.data;
    },

    // 2026-05-05 Phase B — Evidence population summary for one
    // unified requirement. Shape:
    //   { population_size, compliant, non_compliant, compliance_pct,
    //     last_scanned_at, by_test: [...], samples: { total_cards, ... },
    //     is_automated: bool }
    // Renders the "1,402 scanned, 100% compliant" card in the inspector.
    getEvidencePopulation: async (sessionId, requirementKey) => {
        const res = await axios.get(
            `${API_URL}/auditor/sessions/${sessionId}/evidence-population/${encodeURIComponent(requirementKey)}`,
            { headers: getHeaders() }
        );
        return res.data;
    },

    // 2026-05-05 Phase 2d — Bulk-accept inherited reviews. Flips
    // audit_reviews.inheritance_accepted=TRUE atomically for a list of
    // mapped control_ids in one session. Used after a SATISFACTORY
    // verdict propagates across multiple frameworks via intent
    // crosswalk — the auditor reviews them all at once.
    acceptInheritedReviews: async (sessionId, controlIds) => {
        const res = await axios.post(
            `${API_URL}/auditor/accept-inherited`,
            { session_id: sessionId, control_ids: controlIds },
            { headers: getHeaders() }
        );
        return res.data;
    },

    // 2026-05-05 Phase B — Up to N signed evidence cards for a unified
    // requirement, server-side signature-verified. The HMAC key never
    // leaves the backend. Each card has signature_verified: bool.
    getEvidenceSamples: async (sessionId, requirementKey, opts = {}) => {
        const params = {};
        if (opts.limit) params.limit = opts.limit;
        if (opts.includeFailedFirst === false) params.include_failed_first = false;
        const res = await axios.get(
            `${API_URL}/auditor/sessions/${sessionId}/evidence-samples/${encodeURIComponent(requirementKey)}`,
            { headers: getHeaders(), params }
        );
        return res.data;
    },

    saveReview: async (reviewData, sessionId = null) => {
        const res = await axios.post(`${API_URL}/auditor/review`, {
            ...reviewData,
            ...(sessionId && { session_id: sessionId })
        }, {
            headers: getHeaders()
        });
        return res.data;
    },

    getReviews: async (soaVersionId, sessionId = null) => {
        const res = await axios.get(`${API_URL}/auditor/reviews`, {
            headers: getHeaders(),
            params: { soa_version_id: soaVersionId, ...(sessionId && { session_id: sessionId }) }
        });
        return res.data;
    },

    getDashboardStats: async (soaVersionId, sessionId) => {
        const params = { soa_version_id: soaVersionId };
        if (sessionId) params.session_id = sessionId;
        const res = await axios.get(`${API_URL}/auditor/dashboard-stats`, {
            headers: getHeaders(),
            params
        });
        return res.data;
    },
    getVendorEvaluations: async () => {
        const res = await axios.get(`${API_URL}/auditor/vendor-evaluations`, { headers: getHeaders() });
        return res.data?.vendors || [];
    },
    getControlEvidence: async (controlId, sessionId) => {
        const res = await axios.get(`${API_URL}/auditor/control-evidence`, {
            headers: getHeaders(),
            params: { control_id: controlId, ...(sessionId ? { session_id: sessionId } : {}) }
        });
        return res.data;
    },

    // Deploy 2.120 — consolidated common-control matrix (v2) + requirement comment thread.
    getConsolidatedControls: async (sessionId) => {
        const V2 = API_URL.replace('/api/v1', '/api/v2');
        const res = await axios.get(`${V2}/auditor/sessions/${sessionId}/consolidated-controls`, {
            headers: getHeaders(),
        });
        return res.data;
    },
    getRequirementComments: async (requirementKey) => {
        const res = await axios.get(
            `${API_URL}/auditor/requirements/${encodeURIComponent(requirementKey)}/comments`,
            { headers: getHeaders() });
        return res.data;
    },
    postRequirementComment: async (requirementKey, payload) => {
        const res = await axios.post(
            `${API_URL}/auditor/requirements/${encodeURIComponent(requirementKey)}/comments`,
            payload, { headers: getHeaders() });
        return res.data;
    },

    // --- Phase C: evidence-request loop (v2) ---
    createEvidenceRequest: async (requirementKey, payload) => {
        const V2 = API_URL.replace('/api/v1', '/api/v2');
        const res = await axios.post(
            `${V2}/auditor/requirements/${encodeURIComponent(requirementKey)}/evidence-requests`,
            payload, { headers: getHeaders() });
        return res.data;
    },
    getSessionEvidenceRequests: async (sessionId, openOnly = false) => {
        const V2 = API_URL.replace('/api/v1', '/api/v2');
        const res = await axios.get(
            `${V2}/auditor/sessions/${sessionId}/evidence-requests?open_only=${openOnly}`,
            { headers: getHeaders() });
        return res.data?.requests || [];
    },
    getEvidenceRequests: async (openOnly = false) => {
        const V2 = API_URL.replace('/api/v1', '/api/v2');
        const res = await axios.get(`${V2}/auditor/evidence-requests?open_only=${openOnly}`,
            { headers: getHeaders() });
        return res.data?.requests || [];
    },
    respondEvidenceRequest: async (requestId, payload) => {
        const V2 = API_URL.replace('/api/v1', '/api/v2');
        const res = await axios.post(`${V2}/auditor/evidence-requests/${requestId}/respond`,
            payload, { headers: getHeaders() });
        return res.data;
    },
    resolveEvidenceRequest: async (requestId) => {
        const V2 = API_URL.replace('/api/v1', '/api/v2');
        const res = await axios.post(`${V2}/auditor/evidence-requests/${requestId}/resolve`,
            {}, { headers: getHeaders() });
        return res.data;
    },

    // --- Phase C: notifications (bell badge + inbox) ---
    getNotifications: async (unreadOnly = false) => {
        const res = await axios.get(`${API_URL}/notifications?unread_only=${unreadOnly}`,
            { headers: getHeaders() });
        return res.data?.notifications || [];
    },
    getNotificationsUnreadCount: async () => {
        const res = await axios.get(`${API_URL}/notifications/unread-count`,
            { headers: getHeaders() });
        return res.data?.unread || 0;
    },
    markNotificationRead: async (notifId) => {
        const res = await axios.post(`${API_URL}/notifications/${notifId}/read`, {},
            { headers: getHeaders() });
        return res.data;
    },
    markAllNotificationsRead: async () => {
        const res = await axios.post(`${API_URL}/notifications/read-all`, {},
            { headers: getHeaders() });
        return res.data;
    },
    getMySessions: async (status = "ACTIVE") => {
        const res = await axios.get(`${API_URL}/auditor/sessions?session_status=${status}`, {
            headers: getHeaders()
        });
        return res.data;
    },

    getSessionDetail: async (sessionId) => {
        const res = await axios.get(`${API_URL}/auditor/sessions/${sessionId}`, {
            headers: getHeaders()
        });
        return res.data;
    },

    getControlRequirements: async (controlId, intentId = null) => {
        // 2026-05-08 Bug B fix — pass intent_id when called from the
        // unified-intent rail so two intents sharing one underlying
        // control return distinct requirement lists.
        const params = { control_id: controlId };
        if (intentId !== null && intentId !== undefined && intentId !== '') {
            params.intent_id = String(intentId);
        }
        const res = await axios.get(`${API_URL}/auditor/control-requirements`, {
            headers: getHeaders(),
            params
        });
        return res.data;
    },

    // --- NC (Non-Conformance) Management API — Stage 3.8 ---
    raiseNC: async (data) => {
        const res = await axios.post(`${API_URL}/auditor/nc`, data, { headers: getHeaders() });
        return res.data;
    },

    getNCs: async (sessionId, status = null) => {
        const params = { session_id: sessionId };
        if (status) params.status = status;
        const res = await axios.get(`${API_URL}/auditor/nc`, { headers: getHeaders(), params });
        return res.data;
    },

    getNCDetail: async (ncUuid) => {
        const res = await axios.get(`${API_URL}/auditor/nc/${ncUuid}`, { headers: getHeaders() });
        return res.data;
    },

    respondNC: async (ncUuid, data) => {
        const res = await axios.patch(`${API_URL}/auditor/nc/${ncUuid}/respond`, data, { headers: getHeaders() });
        return res.data;
    },

    verifyNC: async (ncUuid, data) => {
        const res = await axios.patch(`${API_URL}/auditor/nc/${ncUuid}/verify`, data, { headers: getHeaders() });
        return res.data;
    },

    getNCCounts: async (sessionId) => {
        const res = await axios.get(`${API_URL}/auditor/nc/counts`, {
            headers: getHeaders(),
            params: { session_id: sessionId }
        });
        return res.data;
    },

    // --- NC Tracking Log (cross-session) API — Stage 3.9 ---
    getAllNCs: async (params = {}) => {
        const res = await axios.get(`${API_URL}/auditor/nc/all`, {
            headers: getHeaders(),
            params
        });
        return res.data;
    },
    getFrameworksList: async () => {
        const res = await axios.get(`${API_URL}/auditor/frameworks-list`, {
            headers: getHeaders()
        });
        return res.data;
    },
    getBusinessAreas: async () => {
        const res = await axios.get(`${API_URL}/auditor/business-areas`, {
            headers: getHeaders()
        });
        return res.data;
    },
    getControlsList: async (frameworkId) => {
        const params = frameworkId ? { framework_id: frameworkId } : {};
        const res = await axios.get(`${API_URL}/auditor/controls-list`, {
            headers: getHeaders(),
            params
        });
        return res.data;
    },
    linkEvidence: async (ncUuid, evidenceIds) => {
        const res = await axios.post(`${API_URL}/auditor/nc/${ncUuid}/link-evidence`,
            { evidence_ids: evidenceIds },
            { headers: getHeaders() }
        );
        return res.data;
    },
    getNCEvidence: async (ncUuid) => {
        const res = await axios.get(`${API_URL}/auditor/nc/${ncUuid}/evidence`, {
            headers: getHeaders()
        });
        return res.data;
    },

    // --- Tenant Users (auditor-accessible — for NC form dropdowns) ---
    getTenantUsersForAuditor: async () => {
        const res = await axios.get(`${API_URL}/auditor/tenant-users`, {
            headers: getHeaders()
        });
        return res.data;
    },

    // --- Tenant Users (admin-only — for user management) ---
    getTenantUsers: async () => {
        const m = window.location.pathname.match(/\/t\/([^/]+)/);
        const tenantId = m ? m[1] : '';
        const res = await axios.get(`${API_URL}/tenants/${tenantId}/users`, {
            headers: getHeaders()
        });
        return res.data;
    },

    // --- Evidence Preview (time-limited token) ---
    getEvidencePreviewToken: async (evidenceId) => {
        const res = await axios.get(`${API_URL}/evidence/preview-token/${evidenceId}`, {
            headers: getHeaders()
        });
        return res.data;
    },

    // --- Stage 14: Auditor Evidence Visibility ---
    getEvidenceView: async (evidenceId) => {
        const res = await axios.get(`${API_URL}/auditor/evidence/${evidenceId}/view`, {
            headers: getHeaders()
        });
        return res.data;
    },

    setEvidenceVerdict: async (evidenceId, data) => {
        const res = await axios.patch(`${API_URL}/auditor/evidence/${evidenceId}/verdict`, data, {
            headers: getHeaders()
        });
        return res.data;
    },

    getEvidenceComments: async (evidenceId) => {
        const res = await axios.get(`${API_URL}/auditor/evidence/${evidenceId}/comments`, {
            headers: getHeaders()
        });
        return res.data;
    },

    postEvidenceComment: async (evidenceId, data) => {
        const res = await axios.post(`${API_URL}/auditor/evidence/${evidenceId}/comments`, data, {
            headers: getHeaders()
        });
        return res.data;
    },

    getEvidenceList: async () => {
        const res = await axios.get(`${API_URL}/evidence`, { headers: getHeaders() });
        return res.data;
    },

    // --- LEGACY SETTINGS API ---
    getSettings: async (section) => {
        const res = await axios.get(`${API_URL}/settings/${section}`, {
            headers: getHeaders()
        });
        return res.data;
    },

    // --- REPORTS API (Stage 3.11) ---
    getReportData: async (slug, params = {}) => {
        // Bounded timeout so a hung/slow backend surfaces as an error instead of
        // spinning forever (bare axios has no default timeout).
        const res = await axios.get(`${API_URL}/reports/${slug}`, {
            headers: getHeaders(),
            params,
            timeout: 45000,
        });
        return res.data;
    },

    downloadReportPdf: async (slug, params = {}) => {
        const res = await axios.get(`${API_URL}/reports/${slug}/pdf`, {
            headers: getHeaders(),
            params,
            responseType: 'blob',
            timeout: 60000,
        });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const disposition = res.headers['content-disposition'];
        const filename = disposition
            ? disposition.split('filename=')[1]?.replace(/"/g, '')
            : `${slug}_report.pdf`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },

    downloadReportCsv: async (slug, params = {}) => {
        const res = await axios.get(`${API_URL}/reports/${slug}/csv`, {
            headers: getHeaders(),
            params,
            responseType: 'blob',
            timeout: 60000,
        });
        const blob = new Blob([res.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const disposition = res.headers['content-disposition'];
        const filename = disposition
            ? disposition.split('filename=')[1]?.replace(/"/g, '')
            : `${slug}_export.csv`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },

    downloadReportDocx: async (slug, params = {}) => {
        // Word render runs an LLM + docx build → generous timeout.
        const res = await axios.get(`${API_URL}/reports/${slug}/docx`, {
            headers: getHeaders(),
            params,
            responseType: 'blob',
            timeout: 120000,
        });
        const blob = new Blob([res.data], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const disposition = res.headers['content-disposition'];
        const filename = disposition
            ? disposition.split('filename=')[1]?.replace(/"/g, '')
            : `${slug}.docx`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },

    // ── Auditor Read-Only: Risk Register, Treatments, Assets ──
    getRiskRegister: async () => {
        const res = await axios.get(`${API_URL}/auditor/risk-register`, { headers: getHeaders() });
        return res.data;
    },
    getRiskDetail: async (riskId) => {
        const res = await axios.get(`${API_URL}/auditor/risk-detail/${riskId}`, { headers: getHeaders() });
        return res.data;
    },
    getTreatments: async (riskId) => {
        const params = riskId ? { risk_id: riskId } : {};
        const res = await axios.get(`${API_URL}/auditor/treatments`, { headers: getHeaders(), params });
        return res.data;
    },
    getAssetRegister: async () => {
        const res = await axios.get(`${API_URL}/auditor/asset-register`, { headers: getHeaders() });
        return res.data;
    },
    getVendorRegister: async () => {
        const res = await axios.get(`${API_URL}/auditor/vendor-register`, { headers: getHeaders() });
        return res.data?.vendors || [];
    },
};
